/**
 * OfferTabs — a titled section + the sticky strip of circular offer thumbnails
 * that doubles as a page navigator. Each thumbnail shows its offer number as a
 * light-blue badge in the top-right corner (no text label). Clicking a tab
 * scrolls to that property's section; active/liked/rejected states are shown via
 * CSS classes. The "Offers" title sits above the strip and scrolls away (only
 * the strip is sticky), so it doesn't affect the scroll offsets.
 *
 * Props:
 *   - offers      {Array}   — offer view-models (uses `id`, `gallery[0]`, `label`)
 *   - activeIndex {number}  — index of the section currently in view
 *   - onSelect    {(i:number)=>void} — scroll to section i
 */
import React, { useEffect, useRef } from 'react';
import { useStrings } from './i18n';
import { cdnSize } from './mapProperty';

export default function OfferTabs({ offers, activeIndex, onSelect }) {
    const t = useStrings();
    const navRef = useRef(null);
    const activeRef = useRef(null);

    // Keep the active offer visible: when activeIndex changes (scroll-spy as you
    // scroll the page, or a tab click), scroll the strip horizontally so the
    // active thumbnail is centred. block:'nearest'/manual scroll avoids moving
    // the page vertically — we only adjust the strip's own horizontal scroll.
    useEffect(() => {
        const nav = navRef.current;
        const el = activeRef.current;
        if (!nav || !el) return;
        const navRect = nav.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const delta = (elRect.left + elRect.width / 2) - (navRect.left + navRect.width / 2);
        nav.scrollTo({ left: nav.scrollLeft + delta, behavior: 'smooth' });
    }, [activeIndex]);

    return (
        <nav className="rs-offers" aria-label={t.propertyOffers} ref={navRef}>
            {offers.map((o, i) => (
                    <button
                        key={o.id}
                        ref={i === activeIndex ? activeRef : null}
                        className={[
                            'rs-offer-tab',
                            i === activeIndex ? 'is-active' : '',
                        ].join(' ')}
                        onClick={() => onSelect(i)}
                        aria-label={o.label} /* keep "Offer N" for screen readers */
                    >
                        <span className="rs-offer-tab__thumb-wrap">
                            <img className="rs-offer-tab__thumb" src={cdnSize(o.gallery[0], 'thumbnails')} alt="" />
                            <span className="rs-offer-tab__badge">{i + 1}</span>
                        </span>
                    </button>
                ))}
        </nav>
    );
}
